/**
 * Date and time formatting, always against an explicit zone.
 *
 * The database stores instants: every timestamp column is `timestamptz`, which
 * records an absolute point in time and renders it in the session's zone (UTC on
 * our project). Nothing in the database remembers that a listing was posted "7pm
 * in Halifax" — only the instant. Turning that instant back into a wall clock is
 * this module's job, and it needs a zone to do it.
 *
 * Every function here takes that zone as a required argument. That is the whole
 * design: the bug this replaces was six call sites doing `new Date().getFullYear()`,
 * which silently uses whatever zone the *server process* happens to run in —
 * America/Halifax on the dev box, UTC on Cloudflare. Same data, two answers,
 * depending on where it rendered. A required parameter makes that impossible to
 * write by accident.
 *
 * No date library. `Intl` does all of this natively and is already in every
 * runtime we target, so a dependency would only add weight.
 */

/**
 * Non-httpOnly on purpose: the client writes it, since the device zone is
 * something only the browser knows. A timezone is not a secret, and the value is
 * validated server-side before it is ever handed to Intl.
 */
export const TIME_ZONE_COOKIE = "ece_tz";

/**
 * Atlantic Time, not UTC.
 *
 * Every member is in Atlantic Canada by design, and Atlantic Time is correct for
 * Nova Scotia, PEI, New Brunswick and Labrador — so this is right for the large
 * majority before anything else is known, and wrong by 30 minutes only for the
 * island of Newfoundland. UTC would be wrong for every single user.
 */
export const DEFAULT_TIME_ZONE = "America/Halifax";

/** Shape of an IANA zone id: "UTC", "America/Halifax", "America/Argentina/Salta". */
const PLAUSIBLE_ZONE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+){0,2}$/;

/**
 * Validated by attempting to construct a formatter rather than against a
 * hardcoded list, because the tz database changes and a stale allowlist would
 * start rejecting real zones.
 *
 * This matters more than it looks: the zone arrives from a cookie, which is
 * untrusted input, and `Intl.DateTimeFormat` throws a RangeError on an unknown
 * zone. Unvalidated, one edited cookie would crash the render of every page that
 * shows a date. The cheap shape check runs first so obvious junk never reaches
 * the constructor.
 */
export function isValidTimeZone(value: string): boolean {
  if (value.length === 0 || value.length > 64) return false;
  if (!PLAUSIBLE_ZONE.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * The zone the browser is set to, or null where that can't be read.
 *
 * Client-side only — there is no equivalent on the server, which is the entire
 * reason the cookie exists.
 */
export function deviceTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/** A bad timestamp costs a label, never a page. */
function instant(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

function partsIn(
  at: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const num = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: num("year"), month: num("month"), day: num("day") };
}

/**
 * A calendar day number *as seen in `timeZone`*, so two instants can be compared
 * by the date a person in that zone would call them.
 *
 * Subtracting milliseconds is not the same question: 11pm Monday and 1am Tuesday
 * are two hours apart and two different days, and "yesterday" has to follow the
 * calendar, not the clock.
 */
function dayNumberIn(at: Date, timeZone: string): number {
  const { year, month, day } = partsIn(at, timeZone);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/** The calendar year in `timeZone`, or null on a malformed timestamp. */
export function yearIn(iso: string, timeZone: string): number | null {
  const at = instant(iso);
  return at ? partsIn(at, timeZone).year : null;
}

/** Today's calendar year in `timeZone`. Used for ages and birth-year ranges. */
export function currentYearIn(timeZone: string, now: number = Date.now()): number {
  return partsIn(new Date(now), timeZone).year;
}

/** "2 Aug 2026" */
export function formatDate(iso: string, timeZone: string): string {
  const at = instant(iso);
  if (!at) return "—";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(at);
}

/**
 * "Wednesday, Jul 29, 2026 10:11 PM".
 *
 * Assembled from `formatToParts` rather than letting a locale lay it out, because
 * two of the pieces are not reliably what they need to be otherwise:
 *
 *   - **Month.** `en-CA` abbreviates September as "Sept" on some ICU builds and
 *     "Sep" on others, and several months carry a trailing period in other
 *     locales. Sliced to three letters with any period stripped, so the width is
 *     the same every month whichever ICU the runtime ships.
 *   - **Day period.** `en-CA` renders "p.m.", not "PM". Normalised here rather than
 *     switching the whole formatter to `en-US`, which would silently change the
 *     other fields too.
 *
 * `hourCycle: "h12"` rather than `hour12: true`: the latter maps midnight to "0"
 * on some ICU versions rather than "12", so "12:30 AM" comes out as "0:30 AM".
 */
export function formatStamp(iso: string, timeZone: string): string {
  const at = instant(iso);
  if (!at) return "—";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h12",
  }).formatToParts(at);
  const p = (type: string) => parts.find((x) => x.type === type)?.value ?? "";

  const month = p("month").replace(/\./g, "").slice(0, 3);
  const period = p("dayPeriod").replace(/[.  ]/g, "").toUpperCase();

  return `${p("weekday")}, ${month} ${p("day")}, ${p("year")} ${p("hour")}:${p("minute")} ${period}`;
}

/** "2 Aug 2026, 7:14 p.m." */
export function formatDateTime(iso: string, timeZone: string): string {
  const at = instant(iso);
  if (!at) return "—";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(at);
}

/**
 * "just now" / "5 minutes ago" / "yesterday" / "2 Aug 2026".
 *
 * The ladder switches to an absolute date after a week, because "37 days ago"
 * makes a reader do arithmetic to answer a question a date answers directly.
 *
 * `numeric: "auto"` is what produces "yesterday" and "tomorrow" instead of
 * "1 day ago" and "in 1 day". Both directions are handled: expiry and renewal
 * dates are in the future, and the same formatter has to serve them.
 */
export function formatRelative(
  iso: string,
  timeZone: string,
  now: number = Date.now(),
): string {
  const at = instant(iso);
  if (!at) return "—";

  const diffMs = at.getTime() - now;
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("en-CA", { numeric: "auto" });

  if (absMs < 60_000) return "just now";
  if (absMs < 3_600_000) {
    return rtf.format(Math.round(diffMs / 60_000), "minute");
  }

  const days = dayNumberIn(at, timeZone) - dayNumberIn(new Date(now), timeZone);
  if (days === 0) return rtf.format(Math.round(diffMs / 3_600_000), "hour");
  if (Math.abs(days) <= 7) return rtf.format(days, "day");
  return formatDate(iso, timeZone);
}
